import { supabase } from '../lib/supabase';

/**
 * Service for managing playbook PDF uploads, tags, viewing, and deletion.
 *
 * Storage layout (private bucket `playbooks`):
 *   {team_id}/{playbook_id}.pdf
 *
 * Phase 1 — no AI integration. Files are served only via short-lived signed URLs.
 */

export interface Playbook {
  id: string;
  team_id: string;
  tag: string;
  filename: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UploadPlaybookInput {
  teamId: string;
  tag: string;
  file: File;
}

export const PLAYBOOK_MAX_BYTES = 10 * 1024 * 1024; // 10MB
export const PLAYBOOK_SIGNED_URL_TTL_SECONDS = 60 * 60; // 60 minutes
const BUCKET = 'playbooks';

function storagePathFor(teamId: string, playbookId: string): string {
  return `${teamId}/${playbookId}.pdf`;
}

class PlaybookService {
  /** List all playbooks for a team, newest first. */
  async list(teamId: string): Promise<Playbook[]> {
    const { data, error } = await supabase
      .from('playbooks')
      .select('*')
      .eq('team_id', teamId)
      .order('uploaded_at', { ascending: false });
    if (error) {
      console.error('[playbookService] list error:', error);
      throw error;
    }
    return (data as Playbook[]) || [];
  }

  /**
   * Upload a PDF for the given team with a coach-defined tag.
   * Creates Storage object first, then inserts the metadata row.
   * Cleans up the orphaned Storage object if the DB insert fails.
   */
  async upload({ teamId, tag, file }: UploadPlaybookInput): Promise<Playbook> {
    const trimmedTag = tag.trim();
    if (!trimmedTag) {
      throw new Error('Tag is required.');
    }
    if (!file) {
      throw new Error('No file provided.');
    }
    if (file.size > PLAYBOOK_MAX_BYTES) {
      throw new Error('File exceeds the 10MB limit.');
    }
    const isPdf =
      file.type === 'application/pdf' ||
      /\.pdf$/i.test(file.name);
    if (!isPdf) {
      throw new Error('Only PDF files are supported.');
    }

    const playbookId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const path = storagePathFor(teamId, playbookId);

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, {
        contentType: 'application/pdf',
        upsert: false,
      });
    if (uploadError) {
      console.error('[playbookService] storage upload error:', uploadError);
      throw new Error(uploadError.message || 'Failed to upload playbook.');
    }

    const { data: userData } = await supabase.auth.getUser();
    const uploadedBy = userData?.user?.id ?? null;

    const { data, error: insertError } = await supabase
      .from('playbooks')
      .insert({
        id: playbookId,
        team_id: teamId,
        tag: trimmedTag,
        filename: file.name,
        file_size: file.size,
        uploaded_by: uploadedBy,
      })
      .select()
      .single();

    if (insertError) {
      console.error('[playbookService] metadata insert error:', insertError);
      // Best-effort cleanup of the orphaned object.
      await supabase.storage.from(BUCKET).remove([path]).catch(() => undefined);
      throw new Error(insertError.message || 'Failed to save playbook metadata.');
    }

    return data as Playbook;
  }

  /** Update a playbook's tag (no Storage operation). */
  async updateTag(playbookId: string, tag: string): Promise<Playbook> {
    const trimmedTag = tag.trim();
    if (!trimmedTag) {
      throw new Error('Tag is required.');
    }
    const { data, error } = await supabase
      .from('playbooks')
      .update({ tag: trimmedTag })
      .eq('id', playbookId)
      .select()
      .single();
    if (error) {
      console.error('[playbookService] updateTag error:', error);
      throw error;
    }
    return data as Playbook;
  }

  /**
   * Delete a playbook: removes Storage object first, then the DB row.
   * If the Storage remove fails the DB row is left intact so the user can retry.
   */
  async delete(playbook: Pick<Playbook, 'id' | 'team_id'>): Promise<void> {
    const path = storagePathFor(playbook.team_id, playbook.id);
    const { error: storageError } = await supabase.storage
      .from(BUCKET)
      .remove([path]);
    if (storageError) {
      // If the object is already missing, continue with the DB delete to clean up.
      const msg = (storageError as { message?: string }).message || '';
      if (!/not\s*found|no such|404/i.test(msg)) {
        console.error('[playbookService] storage delete error:', storageError);
        throw new Error(msg || 'Failed to remove playbook file.');
      }
    }
    const { error: dbError } = await supabase
      .from('playbooks')
      .delete()
      .eq('id', playbook.id);
    if (dbError) {
      console.error('[playbookService] db delete error:', dbError);
      throw dbError;
    }
  }

  /** Generate a fresh signed URL for the viewer. Expires after 60 minutes. */
  async getSignedUrl(playbook: Pick<Playbook, 'id' | 'team_id'>): Promise<string> {
    const path = storagePathFor(playbook.team_id, playbook.id);
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, PLAYBOOK_SIGNED_URL_TTL_SECONDS);
    if (error || !data?.signedUrl) {
      console.error('[playbookService] signed url error:', error);
      throw new Error(error?.message || 'Failed to open playbook.');
    }
    return data.signedUrl;
  }
}

export const playbookService = new PlaybookService();

export function formatPlaybookSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
